import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SessionRow, SessionsProvider } from '../../providers/sessions-provider';
import { SessionItemRow, SessionItemsProvider } from '../../providers/sessionItems-provider';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { SessionsGeneralPanelComponent } from '../session-general-panel/session-general-panel';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { CurrentUser } from "../../../common/providers/current-user";
import { Session } from "../../../common/models/session";
import { Organization } from "../../../common/models/organization";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { SessionBlocComponent } from "../session-bloc/session-bloc";
import { ShareComponent } from "../../../chat/share/share.component";
import { SessionRestComponent } from "../session-rest/session-rest";
import { SessionCommentComponent } from "../session-comment/session-comment";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MatIcon } from "@angular/material/icon";
import { NgForOf, NgIf } from "@angular/common";
import { MatMiniFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { forkJoin, Observable, of, switchMap } from "rxjs";
import { map } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ErrorManagerService } from "../../../common/providers/error-manager-service";
import { LibraryService } from "../../../libraries/providers/library.services";
import { Library } from "../../../common/models/library";
import { SessionItem } from "../../../common/models/session-item";
import { SessionItemWrapper } from "../../../common/models/session-item-wrapper";

@Component({
  selector: 'sessions-session-view',
  providers: [],
  standalone: true,
  styleUrls: ['./session-view.scss'],
  imports: [
    FlexLayoutModule,
    SessionsGeneralPanelComponent,
    MainActionComponent,
    SessionBlocComponent,
    ShareComponent,
    SessionRestComponent,
    SessionCommentComponent,
    ModuleContentComponent,
    MatIcon,
    NgIf,
    NgForOf,
    MatMiniFabButton,
    MatTooltip,
    TranslateModule,
  ],
  templateUrl: './session-view.html'
})
export class SessionsSessionViewComponent implements OnInit, AfterViewInit {
  @Input() public session: Session = {} as Session;
  @Input() public isMobileDisplay = false;
  @Input() public isEditable = false;
  @Input() public isAllCollapsed = false;
  @Input() public hideGeneralPanel = false;
  @Input() public organization: Organization = {} as Organization;
  @Input() public sessionId: number|null = null;

  @Input()
  public showLeftPanel = true;

  public sessionItems: SessionItemRow[] = [];
  public sessionRow: SessionRow | null = null;
  public isWorking = false;
  public library: Library | null = null;

  @ViewChild(SessionsGeneralPanelComponent, {static: false}) public generalPanel: SessionsGeneralPanelComponent | undefined;
  constructor(
    public breadcrumbProvider: BreadcrumbProvider,
    public currentUser: CurrentUser,
    private _sessionsProvider: SessionsProvider,
    private _sessionItemsProvider: SessionItemsProvider,
    private _router: Router,
    private _utils: UtilsProvider,
    private _translater: TranslateService,
    private dialoger: MatDialog,
    private errorManagerService: ErrorManagerService,
    private librariesProvider: LibraryService,
  ) {    }

  public ngOnInit(): void {
    this.loadData().subscribe({
      next: () => {
        this.onToggleAllClicked(this.isAllCollapsed);
      },
      error: this.onError,
    });
  }

  public ngAfterViewInit() {

  }

  public getBlocNumber(sessionItemRow: SessionItemRow): number {
    const allBlocs: SessionItemRow[] = this._utils.findManyIn([SessionItemsProvider.TYPE_BLOC], this.sessionItems, 'type');

    return allBlocs.indexOf(sessionItemRow) + 1;
  }

  public onToggleAllClicked(isCollapsed: boolean): void {
    this.toggleItems(this.sessionItems, isCollapsed);
  }

  public onEditClicked(): void {
    const url = this._router.url + '/editor';
    this.breadcrumbProvider.append({
      title: this._translater.instant('Edition'),
      shortTitle: this._translater.instant('Edition'),
      url
    });
    this._router.navigateByUrl(url + '?backto=' + this._router.url);
  }

  public onClickSaveToLibrary(toDuplicate: SessionRow) {
    const libId = toDuplicate.libraryId ?? this.library?.id ?? 0;
    this._router.navigate([this.organization!.code, 'libraries', libId, 'sessions', 0, 'editor'], {
      queryParams: {
        backto: this._router.url,
        libraryId: libId,
        duplicateAsModel: true,
        modelId: toDuplicate.id,
      }
    });
    for (const dialog of this.dialoger.openDialogs) {
      dialog.close();
    }
  }

  private loadData(): Observable<any> {
    const sessionId = this.sessionId ?? this.session.id;
    if (!sessionId) {
      throw new Error("Session id not found");
    }
    const session$ = (this.session && this.session.sessionItemWrappers)
      ? of(this.session)
      : this._sessionsProvider.getSession({ id: sessionId, filled: true });

    return session$.pipe(
      switchMap((session) => {
        return forkJoin([
          this._sessionsProvider.getSessionRow({organization: this.organization!, session: session, filled: true}),
          this.librariesProvider.getByOrganization(this.organization!),
          this._sessionItemsProvider.buildSessionItemRowsFromSession(this.organization, session),
        ]).pipe(
          map(([sessionRow, library, sessionItems]: [SessionRow, Library|null, SessionItemRow[]]) => {
            this.sessionItems = sessionItems;
            this.sessionRow = sessionRow;
            this.library = library;
          }),
        );
      }),
    );
  }

  private toggleItems(items: SessionItemRow[], isCollapsed: boolean): void {
    items.forEach((item: SessionItemRow) => {
      item.isCollapsed = isCollapsed;
      if (item.innerElements) {
        this.toggleItems(item.innerElements, isCollapsed);
      }
    });
  }

  private onError(error: any): any {
    console.error("SessionView Comp : ", error);
  }
}
